import { Flex, Checkbox, Text, Button, useAppTheme, Box } from '@workwhile/ui'
import React from 'react'
import { DateRangePickerFilter } from 'components/Filter/DateRangePickerFilter'
import { IntFilter } from 'components/Filter/IntFilter'
import { SelectFilter } from 'components/Filter/SelectFilter'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { filtersSchema, FiltersSchema } from './filtersSchema'
import { useUserPaymentsContextValue } from '../../context/UserPaymentsProvider'
import {
  UserPaymentStatus,
  UserPaymentEventType,
  UserPaymentEventSubType,
  UserPaymentProvider,
} from 'api/payments'
import { toTitleCase } from 'lib/string'
import { PaymentStatusRender } from '../UserPaymentsRenderers/PaymentStatusRender'

// Status options
const statusOptions = [
  { label: 'Pending', value: UserPaymentStatus.PENDING },
  { label: 'Completed', value: UserPaymentStatus.COMPLETED },
  { label: 'Failed', value: UserPaymentStatus.FAILED },
  { label: 'Reversed', value: UserPaymentStatus.REVERSED },
  { label: 'Canceled', value: UserPaymentStatus.CANCELED },
]

// Event type options
const eventTypeOptions = [
  { label: 'Shift', value: UserPaymentEventType.SHIFT },
  { label: 'Instant Payout', value: UserPaymentEventType.INSTANT_PAYOUT },
  { label: 'Paycheck', value: UserPaymentEventType.PAYCHECK },
  { label: 'Paycheck W2', value: UserPaymentEventType.PAYCHECK_W2 },
  { label: 'Bonus', value: UserPaymentEventType.BONUS },
  { label: 'Adjustment', value: UserPaymentEventType.ADJUSTMENT },
  { label: 'Reimbursement', value: UserPaymentEventType.REIMBURSEMENT },
  {
    label: 'Automatic Payout',
    value: UserPaymentEventType.AUTOMATIC_PAYOUT,
  },
  { label: 'Everee Payout', value: UserPaymentEventType.EVEREE_PAYOUT },
  { label: 'Payment Refund', value: UserPaymentEventType.PAYMENT_REFUND },
  { label: 'Escheatment', value: UserPaymentEventType.ESCHEATMENT },
  { label: 'W2 Tax Witholding', value: UserPaymentEventType.W2_TAX_WITHOLDING },
  { label: 'W2 Deduction', value: UserPaymentEventType.W2_DEDUCTION },
  { label: 'W2 Net Pay', value: UserPaymentEventType.W2_NET_PAY },
]

// Event sub type options
const eventSubTypeOptions: {
  label: string
  value: 'divider' | UserPaymentEventSubType
}[] = [
  { label: 'Bonus', value: 'divider' },
  { label: 'Elite Bonus', value: UserPaymentEventSubType.ELITE_BONUS },
  {
    label: 'Health Stipend',
    value: UserPaymentEventSubType.HEALTH_STIPEND,
  },

  {
    label: 'Post Shift True Up',
    value: UserPaymentEventSubType.POST_SHIFT_TRUE_UP,
  },
  { label: 'Quest Bonus', value: UserPaymentEventSubType.QUEST_BONUS },
  {
    label: 'Referral Bonus',
    value: UserPaymentEventSubType.REFERRAL_BONUS,
  },
  { label: 'Research', value: UserPaymentEventSubType.RESEARCH },
  {
    label: 'Sick Credit Bonus',
    value: UserPaymentEventSubType.SICK_CREDIT_BONUS,
  },
  { label: 'Signon Bonus', value: UserPaymentEventSubType.SIGNON_BONUS },
  { label: 'Misc', value: 'divider' },
  { label: 'Misc', value: UserPaymentEventSubType.MISC },
  { label: 'Tip', value: UserPaymentEventSubType.TIP },
  { label: 'Toll', value: UserPaymentEventSubType.TOLL },
]

// Type options
const typeOptions = [
  { label: 'W2', value: 'w2' },
  { label: '1099', value: '1099' },
]

// Provider options
const providerOptions = [
  { label: 'Everee', value: UserPaymentProvider.EVEREE },
  { label: 'Stripe', value: UserPaymentProvider.STRIPE },
  { label: 'Marqeta', value: UserPaymentProvider.MARQETA },
  { label: 'BBSI', value: UserPaymentProvider.BBSI },
  { label: 'Highnote', value: UserPaymentProvider.HIGHNOTE },
]

// UserFilters component
const UserFilters = () => {
  return (
    <IntFilter<FiltersSchema>
      fieldPath="user.userId"
      label="User"
      filterKey="user"
      inputLabel="User ID"
    />
  )
}

// StatusInputs component
const StatusInputs = () => {
  const methods = useFormContext<FiltersSchema>()

  const selectedStatuses = methods.watch('status.statuses') || []

  const handleStatusChange = (status: UserPaymentStatus, checked: boolean) => {
    const currentStatuses = selectedStatuses
    if (checked) {
      methods.setValue('status.statuses', [...currentStatuses, status])
    } else {
      methods.setValue(
        'status.statuses',
        currentStatuses.filter((s) => s !== status)
      )
    }
  }

  return (
    <Box>
      <Text fontSize="sm" fontWeight="medium" mb={2}>
        Select Status
      </Text>
      {statusOptions.map((option) => (
        <Checkbox
          key={option.value}
          value={selectedStatuses.includes(option.value)}
          onChange={(checked) => handleStatusChange(option.value, checked)}
        >
          <PaymentStatusRender value={option.value} />
        </Checkbox>
      ))}
    </Box>
  )
}

// StatusFilters component
const StatusFilters = () => {
  return (
    <SelectFilter<FiltersSchema, UserPaymentStatus>
      fieldPath="status.statuses"
      label="Status"
      filterKey="status"
      options={statusOptions}
      inputLabel="Select Status"
      formatSelectedAttributes={(values) =>
        values.map((status) => toTitleCase(status.toLowerCase()))
      }
    >
      <StatusInputs />
    </SelectFilter>
  )
}

// EventTypeFilters component
const EventTypeFilters = () => {
  return (
    <SelectFilter<FiltersSchema, UserPaymentEventType>
      fieldPath="eventType.eventTypes"
      label="Event Type"
      filterKey="event-type"
      options={eventTypeOptions}
      inputLabel="Select Event Types"
    />
  )
}

// EventSubTypeCustomInput component - must be inside FormProvider
const EventSubTypeCustomInput = () => {
  const { colors } = useAppTheme()
  const methods = useFormContext<FiltersSchema>()
  const selectedValues =
    (methods.watch(
      'eventSubType.eventSubTypes'
    ) as UserPaymentEventSubType[]) || []

  const handleValueChange = (
    value: UserPaymentEventSubType,
    checked: boolean
  ) => {
    const currentValues = selectedValues
    if (checked) {
      methods.setValue('eventSubType.eventSubTypes', [
        ...currentValues,
        value,
      ] as any)
    } else {
      methods.setValue(
        'eventSubType.eventSubTypes',
        currentValues.filter((v) => v !== value) as any
      )
    }
  }

  return (
    <Box>
      <Text fontSize="sm" fontWeight="medium" mb={2}>
        Select Event Sub Types
      </Text>
      {eventSubTypeOptions.map((option) =>
        option.value === 'divider' ? (
          <Flex flexDirection="row" key={String(option.value)}>
            {option.label ? (
              <Text my={2} fontWeight="bold">
                {option.label}
              </Text>
            ) : null}
          </Flex>
        ) : (
          <Checkbox
            key={String(option.value)}
            value={selectedValues.includes(
              option.value as UserPaymentEventSubType
            )}
            onChange={(checked) =>
              handleValueChange(
                option.value as UserPaymentEventSubType,
                checked
              )
            }
          >
            {option.label}
          </Checkbox>
        )
      )}
    </Box>
  )
}

// EventSubTypeFilters component
const EventSubTypeFilters = () => {
  return (
    <SelectFilter<FiltersSchema, 'divider' | UserPaymentEventSubType>
      fieldPath="eventSubType.eventSubTypes"
      label="Event Sub Type"
      filterKey="event-sub-type"
      options={eventSubTypeOptions}
      inputLabel="Select Event Sub Types"
    >
      <EventSubTypeCustomInput />
    </SelectFilter>
  )
}

// TypeFilters component
const TypeFilters = () => {
  return (
    <SelectFilter<FiltersSchema, string>
      fieldPath="type.types"
      label="Type"
      filterKey="type"
      options={typeOptions}
      inputLabel="Select Type"
      formatSelectedAttributes={(values) =>
        values.map((type) => (type === 'w2' ? 'W2' : '1099'))
      }
    />
  )
}

// PaymentIdFilters component
const PaymentIdFilters = () => {
  return (
    <IntFilter<FiltersSchema>
      fieldPath="paymentId.id"
      label="ID"
      filterKey="paymentId"
      inputLabel="Payment ID"
    />
  )
}

// PaymentProviderFilters component
const PaymentProviderFilters = () => {
  return (
    <SelectFilter<FiltersSchema, UserPaymentProvider>
      fieldPath="paymentProvider.providers"
      label="Provider"
      filterKey="payment-provider"
      options={providerOptions}
      inputLabel="Select Payment Provider"
      formatSelectedAttributes={(values) =>
        values.map((provider) => toTitleCase(provider.toLowerCase()))
      }
    />
  )
}

// ShiftIdFilters component
const ShiftIdFilters = () => {
  return (
    <IntFilter<FiltersSchema>
      fieldPath="shiftId.id"
      label="Shift"
      filterKey="shiftId"
      inputLabel="Shift ID"
    />
  )
}

// CompanyIdFilters component
const CompanyIdFilters = () => {
  return (
    <IntFilter<FiltersSchema>
      fieldPath="companyId.id"
      label="Company ID"
      filterKey="companyId"
      inputLabel="Company ID"
    />
  )
}

interface UserPaymentFiltersProps {
  hideUserFilter?: boolean
}

export const UserPaymentFilters = ({
  hideUserFilter = false,
}: UserPaymentFiltersProps) => {
  const {
    state: { filters, timeRangeFilters },
    actions: { setFilters, setTimeRangeFilters },
  } = useUserPaymentsContextValue()

  const methods = useForm<FiltersSchema>({
    resolver: zodResolver(filtersSchema),
    reValidateMode: 'onChange',
    mode: 'all',
    defaultValues: {
      timeRange: {
        dateRange: {
          from: timeRangeFilters.startDate || null,
          to: timeRangeFilters.endDate || null,
        },
      },
      user: {
        userId: hideUserFilter ? null : filters.userId,
      },
      status: {
        statuses: filters.status || [],
      },
      eventType: {
        eventTypes: filters.eventType || [],
      },
      type: {
        types: filters.type || [],
      },
      paymentProvider: {
        providers: filters.paymentProvider || [],
      },
      paymentId: {
        id: filters.paymentId || null,
      },
      shiftId: {
        id: filters.shiftId || null,
      },
      companyId: {
        id: filters.companyId || null,
      },
    },
    values: {
      timeRange: {
        dateRange: {
          from: timeRangeFilters.startDate || null,
          to: timeRangeFilters.endDate || null,
        },
      },
      user: {
        userId: hideUserFilter ? null : filters.userId,
      },
      status: {
        statuses: filters.status || [],
      },
      eventType: {
        eventTypes: filters.eventType || [],
      },
      eventSubType: {
        eventSubTypes: filters.eventSubType || [],
      },
      type: {
        types: filters.type || [],
      },
      paymentProvider: {
        providers: filters.paymentProvider || [],
      },
      paymentId: {
        id: filters.paymentId || null,
      },
      shiftId: {
        id: filters.shiftId || null,
      },
      companyId: {
        id: filters.companyId || null,
      },
    },
  })

  const onSubmit = (data: FiltersSchema) => {
    // Update user filters
    setFilters({
      userId: hideUserFilter ? null : data.user.userId,
      status: data.status.statuses,
      eventType: data.eventType.eventTypes,
      eventSubType: data.eventSubType.eventSubTypes,
      type: data.type.types,
      paymentProvider: data.paymentProvider.providers,
      paymentId: data.paymentId.id,
      shiftId: data.shiftId.id,
      companyId: data.companyId.id,
    })

    // Update time range filters
    setTimeRangeFilters({
      startDate: data.timeRange.dateRange.from || undefined,
      endDate: data.timeRange.dateRange.to || undefined,
    })
  }

  return (
    <FormProvider {...methods}>
      <Flex
        as="form"
        flexDirection="row"
        flexWrap="wrap"
        gap={2}
        alignItems="center"
        onSubmit={methods.handleSubmit(onSubmit)}
      >
        <PaymentIdFilters />
        <DateRangePickerFilter<FiltersSchema>
          dateRangeFieldPath="timeRange.dateRange"
          label="Time Range"
          filterKey="time-range"
          placeholder="Select Date range"
        />
        {!hideUserFilter && <UserFilters />}
        <StatusFilters />
        <TypeFilters />
        <EventTypeFilters />
        <EventSubTypeFilters />
        <PaymentProviderFilters />
        <ShiftIdFilters />
        <CompanyIdFilters />
        <Button type="submit" variant="primary">
          Search
        </Button>
      </Flex>
    </FormProvider>
  )
}
